import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, AlertTriangle, CheckCircle2, Loader2, Wrench, RotateCcw, PlayCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const CONF_COLOR = { high: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  medium: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  low: 'bg-red-500/15 text-red-400 border-red-500/30' };

function RepairPanel({ flag, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [repair, setRepair] = useState(null);
  const [note, setNote] = useState('');
  const [rbVersion, setRbVersion] = useState('1');

  const doRepair = async () => {
    setBusy(true);
    try {
      const r = await api.proposeReleaseRepair(flag.walkthrough_id);
      setRepair(r);
      setNote(r.note || '');
      if ((r.proposals || []).length) toast.success('AI repair proposed — review and approve to republish');
      else toast.message(r.note || 'No automatic repair available');
    } catch (e) { toast.error(e.message || 'Repair failed'); }
    finally { setBusy(false); }
  };

  const step = async (label, fn) => {
    try { const r = await fn(); toast.success(`${label} ✓`); return r; }
    catch (e) { toast.error(`${label}: ${e.message}`); throw e; }
  };

  const submit = () => step('Submitted for review', () => api.coachStudioSubmit(repair.draft_id));
  const approve = () => step('Approved', () => api.coachStudioApprove(repair.draft_id))
    .catch(() => toast.message('Separation of duties: the author cannot approve their own repair — a second reviewer with governance rights must approve.'));
  const publish = async () => { await step('Published new version', () => api.coachStudioPublish(repair.draft_id)); onChanged?.(); };

  const doRollback = async () => {
    try {
      const r = await api.releaseRollback(flag.walkthrough_id, parseInt(rbVersion, 10));
      toast.success(`Rolled back → new version ${r.version} (history preserved)`);
      onChanged?.();
    } catch (e) { toast.error(e.message || 'Rollback failed'); }
  };

  return (
    <Card className="p-4 space-y-3" data-testid={`flag-${flag.walkthrough_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" /> {flag.walkthrough_id} <span className="text-xs text-muted-foreground">v{flag.version}</span>
          </p>
          <ul className="mt-1.5 space-y-0.5">
            {(flag.issues || []).map((i, idx) => (
              <li key={idx} className="text-[12px] text-muted-foreground">
                • <span className="text-red-400">{i.code}</span>{i.target ? ` — target "${i.target}"` : ''}{i.renamed_to ? ` (renamed to "${i.renamed_to}")` : ''}
              </li>
            ))}
          </ul>
        </div>
        <Button size="sm" variant="outline" onClick={doRepair} disabled={busy} data-testid={`repair-btn-${flag.walkthrough_id}`} className="gap-1.5 shrink-0">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />} Propose AI repair
        </Button>
      </div>

      {repair && (repair.proposals || []).length > 0 && (
        <div className="rounded-lg border border-border/50 bg-muted/20 p-3 space-y-2" data-testid={`repair-result-${flag.walkthrough_id}`}>
          {repair.proposals.map((p) => (
            <div key={p.step_id} className="text-[12px]">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">{p.step_id}:</span>
                <code className="text-red-400">{p.from}</code> → <code className="text-emerald-400">{p.to}</code>
                <Badge className={`text-[10px] border ${CONF_COLOR[p.confidence] || CONF_COLOR.low}`}>{p.confidence} confidence</Badge>
              </div>
              <p className="text-muted-foreground mt-0.5">{p.rationale}</p>
              {p.evidence && <p className="text-[11px] text-muted-foreground/80 mt-0.5">Evidence: {p.evidence.kind}{p.evidence.removed_at ? ` · removed at ${p.evidence.removed_at}` : ''}</p>}
            </div>
          ))}
          <div className="flex items-center gap-2 text-[11px] pt-1">
            <span className={`px-2 py-0.5 rounded ${repair.validation?.ok ? 'text-emerald-400' : 'text-red-400'}`}>validation {repair.validation?.ok ? 'passed' : 'failed'}</span>
            <span className={`px-2 py-0.5 rounded ${repair.test_results?.ok ? 'text-emerald-400' : 'text-red-400'}`}>test {repair.test_results?.ok ? 'passed' : 'failed'}</span>
          </div>
          <p className="text-[11px] text-amber-400/90">{note}</p>
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <Button size="sm" variant="secondary" onClick={submit} data-testid={`submit-${flag.walkthrough_id}`}>Submit for review</Button>
            <Button size="sm" variant="secondary" onClick={approve} data-testid={`approve-${flag.walkthrough_id}`}>Approve</Button>
            <Button size="sm" onClick={publish} data-testid={`publish-${flag.walkthrough_id}`}>Publish new version</Button>
          </div>
        </div>
      )}
      {repair && (repair.proposals || []).length === 0 && (
        <p className="text-[12px] text-amber-400" data-testid={`repair-none-${flag.walkthrough_id}`}>{note}</p>
      )}

      <div className="flex items-center gap-2 pt-1 border-t border-border/40">
        <span className="text-[11px] text-muted-foreground">Rollback to version</span>
        <Input value={rbVersion} onChange={(e) => setRbVersion(e.target.value)} className="h-7 w-16 text-xs" data-testid={`rollback-version-${flag.walkthrough_id}`} />
        <Button size="sm" variant="ghost" onClick={doRollback} data-testid={`rollback-${flag.walkthrough_id}`} className="gap-1.5 text-muted-foreground">
          <RotateCcw className="w-3.5 h-3.5" /> Rollback
        </Button>
      </div>
    </Card>
  );
}

export default function AdminReleaseValidationPage() {
  const [pv, setPv] = useState('1.0');
  const [latest, setLatest] = useState('');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const loadAlerts = async () => {
    try { const a = await api.getReleaseAlerts(); setAlerts(a.alerts || []); } catch (_) { /* ignore */ }
  };

  const load = async () => {
    try {
      const [cfg, rep] = await Promise.all([api.getReleaseConfig(), api.getReleaseReport()]);
      setPv(cfg.product_version || '1.0');
      setLatest(cfg.latest_product_version || '');
      if (rep.latest_run) setReport(rep.latest_run);
    } catch (e) { toast.error(e.message || 'Could not load release config'); }
    finally { setLoading(false); }
    loadAlerts();
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true);
    try {
      // Setting the product version AUTO-VALIDATES server-side and returns the report + any new breaks.
      const rep = await api.setReleaseConfig({ product_version: pv });
      setReport(rep);
      if ((rep.new_breaks || []).length) toast.error(`${rep.new_breaks.length} new break(s) detected for product ${pv}`);
      else toast.success(`Validated ${rep.total} walkthroughs — ${rep.flagged_count} flagged`);
      loadAlerts();
    } catch (e) { toast.error(e.message || 'Validation failed'); }
    finally { setRunning(false); }
  };

  const ack = async (id) => {
    try { await api.ackReleaseAlert(id); setAlerts((a) => a.filter((x) => x.alert_id !== id)); }
    catch (e) { toast.error(e.message || 'Could not dismiss alert'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6" data-testid="admin-release-validation">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-primary" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Coach Release Validation</h1>
          <p className="text-sm text-muted-foreground">Validate published walkthroughs against a product version, repair broken content, and roll back.</p>
        </div>
      </div>

      {alerts.map((a) => (
        <Card key={a.alert_id} className="p-4 border-amber-500/40 bg-amber-500/[0.06]" data-testid={`release-alert-${a.alert_id}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-amber-500 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {a.new_breaks.length} new break{a.new_breaks.length > 1 ? 's' : ''} on release {a.product_version}
                {a.from_version ? <span className="text-xs text-muted-foreground font-normal">(moved from {a.from_version})</span> : null}
              </p>
              <p className="text-[12px] text-muted-foreground mt-1">{a.new_breaks.map((b) => b.walkthrough_id).join(', ')}</p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => ack(a.alert_id)} data-testid={`alert-ack-${a.alert_id}`}>Dismiss</Button>
          </div>
        </Card>
      ))}

      <Card className="p-5 flex items-end gap-3 flex-wrap">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Product version to validate</label>
          <Input value={pv} onChange={(e) => setPv(e.target.value)} className="h-9 w-28" data-testid="release-version-input" />
        </div>
        {latest && <span className="text-[11px] text-muted-foreground mb-2.5">latest release: {latest}</span>}
        <Button onClick={run} disabled={running} data-testid="run-validation-btn" className="gap-2 ml-auto">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Run validation
        </Button>
      </Card>

      {report && (
        <>
          <div className="grid grid-cols-3 gap-3" data-testid="impact-summary">
            <Card className="p-4"><p className="text-xs text-muted-foreground">Total</p><p className="text-2xl font-semibold text-foreground">{report.total}</p></Card>
            <Card className="p-4"><p className="text-xs text-muted-foreground">Passing</p><p className="text-2xl font-semibold text-emerald-400">{report.ok}</p></Card>
            <Card className="p-4"><p className="text-xs text-muted-foreground">Flagged</p><p className="text-2xl font-semibold text-amber-400" data-testid="flagged-count">{report.flagged_count}</p></Card>
          </div>

          {(report.broken_targets || []).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Broken targets:</span>
              {report.broken_targets.map((t) => <Badge key={t} className="bg-red-500/15 text-red-400 border-red-500/30 text-xs">{t}</Badge>)}
            </div>
          )}

          {report.flagged_count === 0 ? (
            <Card className="p-6 flex items-center gap-2 text-emerald-400" data-testid="all-clear"><CheckCircle2 className="w-5 h-5" /> All published walkthroughs are valid for product {report.product_version}.</Card>
          ) : (
            <div className="space-y-3">
              {report.flagged.map((f) => <RepairPanel key={f.walkthrough_id} flag={f} onChanged={run} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
